import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cognitoAuthService, RegisterData } from '../services/cognitoAuth';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const RegisterPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const history = useHistory();
  const [currentStep, setCurrentStep] = useState(1);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);
  const [registerForm, setRegisterForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    acceptTerms: false,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setRegisterForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!registerForm.email) {
      errors.email = t('validation.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(registerForm.email)) {
      errors.email = t('validation.emailInvalid');
    }

    if (!registerForm.password) {
      errors.password = t('validation.passwordRequired');
    } else if (registerForm.password.length < 8) {
      errors.password = t('validation.passwordLength');
    }

    if (!registerForm.confirmPassword) {
      errors.confirmPassword = t('validation.confirmPasswordRequired');
    } else if (registerForm.password !== registerForm.confirmPassword) {
      errors.confirmPassword = t('validation.passwordsMismatch');
    }

    if (!registerForm.name) {
      errors.name = t('validation.nameRequired');
    }

    if (!registerForm.acceptTerms) {
      errors.acceptTerms = t('validation.termsRequired');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const registerData: RegisterData = {
        email: registerForm.email,
        password: registerForm.password,
        name: registerForm.name,
      };

      const result = await cognitoAuthService.register(registerData);

      if (result.success) {
        setCurrentStep(2);
      } else {
        // Handle case where user exists but hasn't verified email
        if (result.errorCode === 'UsernameExistsException') {
          setSuccess(t('messages.accountExists'));
          // Automatically resend verification code
          try {
            await cognitoAuthService.resendConfirmationCode(registerForm.email);
          } catch (resendErr) {
            console.error('Failed to resend code:', resendErr);
          }
          // Move to verification step
          setCurrentStep(2);
        } else {
          setError(result.message || t('messages.registrationError'));
        }
      }
    } catch (err) {
      console.error('Registration failed:', err);
      setError(t('messages.registrationError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!verificationCode.trim()) {
      setError(t('validation.verificationCodeRequired'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await cognitoAuthService.confirmRegistration(
        registerForm.email,
        verificationCode,
      );

      if (result.success) {
        setSuccess(t('messages.verificationSuccess'));

        // Auto-login the user after successful verification
        const loginResult = await cognitoAuthService.login({
          email: registerForm.email,
          password: registerForm.password,
        });

        if (loginResult.success) {
          window.location.href = '/tracks';
        } else {
          setSuccess(t('messages.verificationSuccessLogin'));
          setTimeout(() => {
            history.push('/login');
          }, 2000);
        }
      } else {
        setError(result.message || t('messages.verificationError'));
      }
    } catch (err) {
      console.error('Verification failed:', err);
      setError(t('messages.verificationError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await cognitoAuthService.resendConfirmationCode(
        registerForm.email,
      );

      if (result.success) {
        setSuccess(t('messages.codeSent'));
      } else {
        setError(result.message || t('messages.codeResendError'));
      }
    } catch (err) {
      console.error('Resend code failed:', err);
      setError(t('messages.codeResendError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleRegister = () => {
    try {
      cognitoAuthService.loginWithGoogle();
    } catch (err) {
      console.error('Google registration failed:', err);
      setError(t('messages.googleError'));
    }
  };

  const handleFacebookRegister = () => {
    try {
      cognitoAuthService.loginWithFacebook();
    } catch (err) {
      console.error('Facebook registration failed:', err);
      setError(t('messages.facebookError'));
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError(null);
      setSuccess(null);
    }
  };

  if (isLoading) {
    return (
      <div
        className='min-h-screen flex items-center justify-center'
        style={{ background: 'rgb(8 6 20 / 100%)' }}
      >
        <div className='text-center animate-fade-in'>
          <div className='spinner glow mx-auto mb-6'></div>
          <p className='text-base' style={{ color: 'var(--text-secondary)' }}>
            {currentStep === 1
              ? t('register.creatingAccount')
              : t('register.verifyingEmail')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: 'rgb(8 6 20 / 100%)',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SEO
        title={tCommon('seo.register.title')}
        description={tCommon('seo.register.description')}
        canonical='/register'
      />
      <Header variant='default' />
      <div
        className='flex items-center justify-center background-register'
        style={{ paddingTop: '80px', flex: 1 }}
      >
        <div className='register-background-overlay'></div>
        <div
          className='max-w-md w-full space-y-4 md:space-y-8 p-4 md:p-8 animate-fade-in'
          style={{ zIndex: 2 }}
        >
          <div className='dark-card mb-8'>
            <div className='text-center mb-4 md:mb-8'>
              <h2
                className='text-xl sm:text-2xl md:text-3xl font-semibold mb-2'
                style={{
                  color: 'var(--text-primary)',
                  fontFamily: 'Lexend Deca, sans-serif',
                  margin: 0,
                }}
              >
                {currentStep === 1
                  ? t('register.title')
                  : t('register.verifyTitle')}
              </h2>
              <p
                style={{
                  color: '#fff',
                  margin: 0,
                  lineHeight: 1.4,
                }}
              >
                {currentStep === 1
                  ? t('register.subtitle')
                  : t('register.verifySubtitle')}
              </p>
            </div>

            {error && (
              <div className='mb-4 p-4 bg-red-900/20 border border-red-800 text-red-200 rounded-lg'>
                {error}
              </div>
            )}

            {success && (
              <div className='mb-4 p-4 bg-green-900/20 border border-green-800 text-green-200 rounded-lg'>
                {success}
              </div>
            )}

            {/* Step 1: Registration Form */}
            {currentStep === 1 && (
              <>
                <div
                  className='flex flex-col items-center text-sm'
                  style={{ gap: '8px' }}
                >
                  <button
                    onClick={handleGoogleRegister}
                    disabled={isLoading}
                    className='btn btn-primary w-full'
                  >
                    <svg className='w-5 h-5 mr-2' viewBox='0 0 24 24'>
                      <path
                        fill='currentColor'
                        d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                      />
                      <path
                        fill='currentColor'
                        d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                      />
                      <path
                        fill='currentColor'
                        d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                      />
                      <path
                        fill='currentColor'
                        d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                      />
                    </svg>
                    {t('login.continueWithGoogle')}
                  </button>

                  <button
                    onClick={handleFacebookRegister}
                    disabled={isLoading}
                    className='btn btn-secondary w-full'
                  >
                    <svg
                      className='w-5 h-5 mr-2'
                      fill='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path d='M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' />
                    </svg>
                    {t('login.continueWithFacebook')}
                  </button>
                </div>

                <div className='mt-6'>
                  <div className='divider'></div>
                  <button
                    type='button'
                    onClick={() => setShowEmailForm((prev) => !prev)}
                    disabled={isLoading}
                    className='btn btn-primary w-full'
                    aria-expanded={showEmailForm}
                  >
                    <svg
                      className='w-5 h-5 mr-2'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-2 11H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2z'
                      />
                    </svg>
                    {t('register.registerWithEmail')}
                  </button>
                </div>

                {showEmailForm && (
                  <div className='mt-6 animate-fade-in'>
                    <form onSubmit={handleSubmit}>
                      <div className='mb-2'>
                        <label
                          htmlFor='name'
                          className='block text-sm font-medium text-white mb-2'
                        >
                          {t('register.name')}
                        </label>
                        <input
                          id='name'
                          name='name'
                          type='text'
                          value={registerForm.name}
                          onChange={handleInputChange}
                          className={`input ${
                            formErrors.name ? 'border-red-300' : ''
                          }`}
                          placeholder={t('register.namePlaceholder')}
                        />
                        {formErrors.name && (
                          <p className='mt-2 text-sm text-red-600'>
                            {formErrors.name}
                          </p>
                        )}
                      </div>

                      <div className='mb-2'>
                        <label
                          htmlFor='email'
                          className='block text-sm font-medium text-white mb-2'
                        >
                          {t('register.email')}
                        </label>
                        <input
                          id='email'
                          name='email'
                          type='email'
                          value={registerForm.email}
                          onChange={handleInputChange}
                          className={`input ${
                            formErrors.email ? 'border-red-300' : ''
                          }`}
                          placeholder={t('register.emailPlaceholder')}
                        />
                        {formErrors.email && (
                          <p className='mt-2 text-sm text-red-600'>
                            {formErrors.email}
                          </p>
                        )}
                      </div>

                      <div className='mb-2'>
                        <label
                          htmlFor='password'
                          className='block text-sm font-medium text-white mb-2'
                        >
                          {t('register.password')}
                        </label>
                        <div style={{ position: 'relative' }}>
                          <input
                            id='password'
                            name='password'
                            type={showPassword ? 'text' : 'password'}
                            value={registerForm.password}
                            onChange={handleInputChange}
                            className={`input ${
                              formErrors.password ? 'border-red-300' : ''
                            }`}
                            style={{ paddingRight: '2.75rem' }}
                            placeholder={t('register.passwordPlaceholder')}
                          />
                          <button
                            type='button'
                            onClick={() => setShowPassword((prev) => !prev)}
                            style={{
                              position: 'absolute',
                              right: '0.75rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: 'var(--text-secondary)',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                            tabIndex={-1}
                            aria-label={
                              showPassword ? 'Hide password' : 'Show password'
                            }
                          >
                            {showPassword ? (
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='20'
                                height='20'
                                fill='none'
                                viewBox='0 0 24 24'
                                stroke='currentColor'
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21'
                                />
                              </svg>
                            ) : (
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='20'
                                height='20'
                                fill='none'
                                viewBox='0 0 24 24'
                                stroke='currentColor'
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                                />
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                                />
                              </svg>
                            )}
                          </button>
                        </div>
                        {formErrors.password && (
                          <p className='mt-2 text-sm text-red-600'>
                            {formErrors.password}
                          </p>
                        )}
                      </div>

                      <div className='mb-2'>
                        <label
                          htmlFor='confirmPassword'
                          className='block text-sm font-medium text-white mb-2'
                        >
                          {t('register.confirmPassword')}
                        </label>
                        <div style={{ position: 'relative' }}>
                          <input
                            id='confirmPassword'
                            name='confirmPassword'
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={registerForm.confirmPassword}
                            onChange={handleInputChange}
                            className={`input ${
                              formErrors.confirmPassword ? 'border-red-300' : ''
                            }`}
                            style={{ paddingRight: '2.75rem' }}
                            placeholder={t(
                              'register.confirmPasswordPlaceholder',
                            )}
                          />
                          <button
                            type='button'
                            onClick={() =>
                              setShowConfirmPassword((prev) => !prev)
                            }
                            style={{
                              position: 'absolute',
                              right: '0.75rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: 'var(--text-secondary)',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                            tabIndex={-1}
                            aria-label={
                              showConfirmPassword
                                ? 'Hide password'
                                : 'Show password'
                            }
                          >
                            {showConfirmPassword ? (
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='20'
                                height='20'
                                fill='none'
                                viewBox='0 0 24 24'
                                stroke='currentColor'
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21'
                                />
                              </svg>
                            ) : (
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='20'
                                height='20'
                                fill='none'
                                viewBox='0 0 24 24'
                                stroke='currentColor'
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                                />
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                                />
                              </svg>
                            )}
                          </button>
                        </div>
                        {formErrors.confirmPassword && (
                          <p className='mt-2 text-sm text-red-600'>
                            {formErrors.confirmPassword}
                          </p>
                        )}
                      </div>

                      <div className='flex items-center mt-6'>
                        <input
                          id='acceptTerms'
                          name='acceptTerms'
                          type='checkbox'
                          checked={registerForm.acceptTerms}
                          onChange={handleInputChange}
                          className='h-4 w-4 text-white-600 rounded'
                        />
                        <label
                          htmlFor='acceptTerms'
                          className='ml-2 block text-sm text-white'
                        >
                          {t('register.acceptTerms')}{' '}
                          <Link to='/terms' className='text-white-600'>
                            {t('login.termsOfService')}
                          </Link>{' '}
                          {t('login.and')}{' '}
                          <Link to='/privacy' className='text-white-600'>
                            {t('login.privacyPolicy')}
                          </Link>
                        </label>
                      </div>
                      {formErrors.acceptTerms && (
                        <p className='text-sm text-red-600'>
                          {formErrors.acceptTerms}
                        </p>
                      )}

                      <div className='text-center mt-6'>
                        <button
                          type='submit'
                          disabled={isLoading}
                          className='btn btn-primary'
                          style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid',
                          }}
                        >
                          {t('register.submit')}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </>
            )}

            {/* Step 2: Email Verification */}
            {currentStep === 2 && (
              <div className='space-y-4'>
                <div className='p-4 dark-card'>
                  <p className='text-sm text-white mb-4'>
                    {t('register.codeSent')}{' '}
                    <strong>{registerForm.email}</strong>.{' '}
                    {t('register.enterCode')}
                  </p>

                  <form onSubmit={handleVerificationSubmit}>
                    <div>
                      <label
                        htmlFor='verificationCode'
                        className='block text-sm font-medium text-white mb-2'
                      >
                        {t('register.verificationCode')}
                      </label>
                      <input
                        id='verificationCode'
                        type='text'
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        className='input text-center text-lg tracking-widest mb-2'
                        placeholder='000000'
                        maxLength={6}
                      />
                    </div>
                    <div className='flex'>
                      <button
                        type='button'
                        onClick={handleResendCode}
                        disabled={isLoading}
                        className='text-white-600 underline flex-1 mb-6'
                        style={{
                          textDecoration: 'underline',
                          fontSize: 14,
                        }}
                      >
                        {t('register.resendCode')}
                      </button>
                    </div>
                    <div className='flex space-x-3'>
                      <button
                        type='submit'
                        disabled={isLoading}
                        className='btn btn-secondary flex-1'
                      >
                        {t('register.verifyEmail')}
                      </button>
                    </div>
                  </form>
                </div>

                <div className='flex justify-start'>
                  <button
                    onClick={goToPreviousStep}
                    className='btn btn-primary'
                  >
                    ← {t('register.previous')}
                  </button>
                </div>
              </div>
            )}

            {/* Footer - only show on step 1 */}
            {currentStep === 1 && (
              <div className='mt-6 text-center'>
                <div className='divider'></div>

                <p className='text-sm text-white'>
                  {t('register.alreadyHaveAccount')}{' '}
                  <Link to='/login' className='text-white-600 font-medium'>
                    {t('register.loginHere')}
                  </Link>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RegisterPage;
